import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowDown, ArrowUp, Bookmark, ChevronLeft, ChevronRight, Columns3, Download, RefreshCw, Search } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Input } from "../components/ui/input";
import { boApi, download, errMsg } from "./api";
import { useBo } from "./AdminAuth";
import { Spinner } from "./ui";

const store = (k, v) => (v === undefined ? JSON.parse(localStorage.getItem(k) || "null") : localStorage.setItem(k, JSON.stringify(v)));

function toCsv(rows, cols) {
  const esc = (v) => { let s = v == null ? "" : typeof v === "object" ? JSON.stringify(v) : String(v); if (/^[=+\-@]/.test(s)) s = `'${s}`; return `"${s.replace(/"/g, '""')}"`; };
  return [cols.map((c) => esc(c.label)).join(","), ...rows.map((r) => cols.map((c) => esc(r[c.key])).join(","))].join("\n");
}

function FilterBar({ filters, values, set }) {
  return filters.map((f) => (
    f.type === "select" ? (
      <select key={f.key} data-testid={`filter-${f.key}`} value={values[f.key] ?? ""} onChange={(e) => set(f.key, e.target.value)} className="h-8 rounded-md border border-input bg-background px-2 text-xs">
        <option value="">{f.label}: all</option>
        {f.options.map((o) => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
      </select>
    ) : (
      <label key={f.key} className="flex items-center gap-1 text-[11px] text-muted-foreground">{f.label}
        <Input data-testid={`filter-${f.key}`} type={f.type || "text"} value={values[f.key] ?? ""} onChange={(e) => set(f.key, e.target.value)} className="h-8 w-auto px-2 text-xs" />
      </label>
    )
  ));
}

function ColumnChooser({ columns, hidden, setHidden }) {
  return (
    <Popover>
      <PopoverTrigger asChild><button data-testid="table-columns-btn" className="flex h-8 items-center gap-1 rounded-md border border-border px-2 text-xs hover:bg-accent"><Columns3 className="h-3.5 w-3.5" />Columns</button></PopoverTrigger>
      <PopoverContent className="w-56 p-2">
        {columns.map((c) => (
          <label key={c.key} className="flex items-center gap-2 rounded px-1.5 py-1 text-xs hover:bg-accent">
            <input type="checkbox" checked={!hidden.includes(c.key)} onChange={(e) => setHidden(e.target.checked ? hidden.filter((x) => x !== c.key) : [...hidden, c.key])} />{c.label}
          </label>
        ))}
      </PopoverContent>
    </Popover>
  );
}

function SavedFilters({ id, values, apply }) {
  const [saved, setSaved] = useState(() => store(`bo_sf_${id}`) || []);
  const save = () => {
    const name = window.prompt("Name for this filter");
    if (!name) return;
    const next = [...saved.filter((s) => s.name !== name), { name, values }];
    setSaved(next);
    store(`bo_sf_${id}`, next);
  };
  return (
    <Popover>
      <PopoverTrigger asChild><button data-testid="table-saved-filters-btn" className="flex h-8 items-center gap-1 rounded-md border border-border px-2 text-xs hover:bg-accent"><Bookmark className="h-3.5 w-3.5" />Saved</button></PopoverTrigger>
      <PopoverContent className="w-56 p-2 text-xs">
        <button data-testid="save-filter-btn" onClick={save} className="mb-1 w-full rounded bg-primary/15 px-2 py-1 text-left text-primary">Save current filters…</button>
        {saved.length === 0 && <div className="px-2 py-1 text-muted-foreground">No saved filters</div>}
        {saved.map((s) => (
          <div key={s.name} className="flex items-center justify-between rounded px-2 py-1 hover:bg-accent">
            <button onClick={() => apply(s.values)}>{s.name}</button>
            <button className="text-muted-foreground" onClick={() => { const n = saved.filter((x) => x.name !== s.name); setSaved(n); store(`bo_sf_${id}`, n); }}>×</button>
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );
}

export function DataTable({ id, endpoint, columns, filters = [], fixed = {}, local = false, rowKey = "id", onRowClick, actions, size = 25, reloadKey, defaultSort, toolbar, transform }) {
  const { can } = useBo();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [fv, setFv] = useState({});
  const [sort, setSort] = useState(defaultSort || null);
  const [busy, setBusy] = useState(true);
  const [hidden, setHiddenS] = useState(() => store(`bo_cols_${id}`) || columns.filter((c) => c.hidden).map((c) => c.key));
  const [tick, setTick] = useState(0);
  const setHidden = (h) => { setHiddenS(h); store(`bo_cols_${id}`, h); };
  const params = useMemo(() => ({ ...Object.fromEntries(Object.entries(fv).filter(([, v]) => v !== "" && v != null)), ...fixed, q: q || undefined }), [fv, fixed, q]);
  const pkey = JSON.stringify(params);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const p = JSON.parse(pkey);
      const r = await boApi.get(endpoint, { params: local ? p : { ...p, page, size, sort: sort?.key, dir: sort?.dir } });
      const data = transform ? transform(r.data) : r.data;
      if (local) { setRows(data); setTotal(data.length); } else { setRows(data.rows); setTotal(data.total); }
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  }, [endpoint, pkey, page, size, sort, local, transform]);
  useEffect(() => { load(); }, [load, reloadKey, tick]);
  useEffect(() => { setPage(1); }, [pkey]);

  const visible = columns.filter((c) => !hidden.includes(c.key));
  let shown = rows;
  if (local) {
    if (q) shown = shown.filter((r) => JSON.stringify(r).toLowerCase().includes(q.toLowerCase()));
    if (sort) shown = [...shown].sort((a, b) => ((a[sort.key] ?? "") > (b[sort.key] ?? "") ? 1 : -1) * (sort.dir === "asc" ? 1 : -1));
  }
  const pages = Math.max(1, Math.ceil((local ? shown.length : total) / size));
  const pageRows = local ? shown.slice((page - 1) * size, page * size) : shown;
  const exp = async (fmt) => {
    try {
      if (local) {
        const blob = new Blob([toCsv(shown, visible)], { type: "text/csv" });
        const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${id}.csv`; a.click();
      } else await download(endpoint, { ...params, sort: sort?.key, dir: sort?.dir, format: fmt }, `${id}.${fmt}`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggleSort = (c) => c.sortable !== false && setSort((s) => (s?.key === c.key ? { key: c.key, dir: s.dir === "asc" ? "desc" : "asc" } : { key: c.key, dir: "desc" }));

  return (
    <div data-testid={`table-${id}`} className="rounded-lg border border-border bg-card">
      <div className="flex flex-wrap items-center gap-2 border-b border-border p-2.5">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-2 h-3.5 w-3.5 text-muted-foreground" />
          <Input data-testid={`table-${id}-search`} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="h-8 w-48 pl-7 text-xs" />
        </div>
        <FilterBar filters={filters} values={fv} set={(k, v) => setFv((s) => ({ ...s, [k]: v }))} />
        <div className="ml-auto flex items-center gap-1.5">
          {toolbar}
          {filters.length > 0 && <SavedFilters id={id} values={fv} apply={setFv} />}
          <ColumnChooser columns={columns} hidden={hidden} setHidden={setHidden} />
          <button data-testid={`table-${id}-refresh`} onClick={() => setTick((t) => t + 1)} className="flex h-8 items-center rounded-md border border-border px-2 hover:bg-accent"><RefreshCw className={`h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} /></button>
          {can("export") && <>
            <button data-testid={`table-${id}-export-csv`} onClick={() => exp("csv")} className="flex h-8 items-center gap-1 rounded-md border border-border px-2 text-xs hover:bg-accent"><Download className="h-3.5 w-3.5" />CSV</button>
            {!local && <button data-testid={`table-${id}-export-xlsx`} onClick={() => exp("xlsx")} className="flex h-8 items-center gap-1 rounded-md border border-border px-2 text-xs hover:bg-accent">Excel</button>}
          </>}
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12px]">
          <thead className="sticky top-0 bg-muted/60 text-[10.5px] uppercase tracking-wider text-muted-foreground">
            <tr>
              {visible.map((c) => (
                <th key={c.key} onClick={() => toggleSort(c)} className={`cursor-pointer select-none whitespace-nowrap px-3 py-2 font-semibold ${c.right ? "text-right" : "text-left"}`}>
                  <span className="inline-flex items-center gap-1">{c.label}{sort?.key === c.key && (sort.dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />)}</span>
                </th>
              ))}
              {actions && <th className="px-3 py-2 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {busy && rows.length === 0 && <tr><td colSpan={99} className="p-6 text-center"><Spinner className="mx-auto" /></td></tr>}
            {!busy && pageRows.length === 0 && <tr><td colSpan={99} data-testid={`table-${id}-empty`} className="p-6 text-center text-muted-foreground">No records</td></tr>}
            {pageRows.map((r, i) => (
              <tr key={r[rowKey] ?? i} data-testid={`row-${id}-${i}`} onClick={() => onRowClick?.(r)} className={`border-t border-border/60 transition-colors duration-150 hover:bg-accent/40 ${onRowClick ? "cursor-pointer" : ""}`}>
                {visible.map((c) => <td key={c.key} className={`whitespace-nowrap px-3 py-1.5 ${c.right ? "num text-right" : ""}`}>{c.render ? c.render(r[c.key], r) : (r[c.key] ?? "—")}</td>)}
                {actions && <td className="px-3 py-1 text-right" onClick={(e) => e.stopPropagation()}><div className="flex justify-end gap-1">{actions(r, () => setTick((t) => t + 1))}</div></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between border-t border-border px-3 py-2 text-xs text-muted-foreground">
        <span data-testid={`table-${id}-total`}>{local ? shown.length : total} records</span>
        <div className="flex items-center gap-2">
          <button data-testid={`table-${id}-prev`} disabled={page <= 1} onClick={() => setPage(page - 1)} className="rounded border border-border p-1 disabled:opacity-40"><ChevronLeft className="h-3.5 w-3.5" /></button>
          <span>Page {page} / {pages}</span>
          <button data-testid={`table-${id}-next`} disabled={page >= pages} onClick={() => setPage(page + 1)} className="rounded border border-border p-1 disabled:opacity-40"><ChevronRight className="h-3.5 w-3.5" /></button>
        </div>
      </div>
    </div>
  );
}
